import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, X, ChevronDown } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const STORAGE_KEY = 'geo_currency_banner_dismissed';

export default function GeoCurrencyBanner() {
  const { t } = useTranslation();
  const { currency, setCurrency, geoDetected, currencyConfig, allCurrencies } = useCurrency();
  const [visible, setVisible] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (!geoDetected) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed === 'true') return;
    const timer = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(timer);
  }, [geoDetected]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleChangeCurrency = (code) => {
    setCurrency(code);
    setShowPicker(false);
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  if (!geoDetected) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="overflow-hidden"
          data-testid="geo-currency-banner"
        >
          <div className="bg-teal-500/10 border-b border-teal-500/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <MapPin className="w-4 h-4 text-teal-400 flex-shrink-0" />
                <p className="text-sm text-slate-300 truncate" data-testid="geo-currency-text">
                  {t('currency.geoDetected', { symbol: currencyConfig.symbol, code: currency })}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative">
                  <button
                    onClick={() => setShowPicker(!showPicker)}
                    className="text-xs font-medium text-teal-400 hover:text-teal-300 bg-teal-500/15 hover:bg-teal-500/25 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                    data-testid="geo-currency-change-btn"
                  >
                    {t('currency.change')}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  <AnimatePresence>
                    {showPicker && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="absolute right-0 top-full mt-1 w-36 bg-slate-900 border border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
                      >
                        {Object.entries(allCurrencies).map(([code, cfg]) => (
                          <button
                            key={code}
                            onClick={() => handleChangeCurrency(code)}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors ${
                              code === currency
                                ? 'bg-teal-500/20 text-teal-400'
                                : 'text-slate-300 hover:bg-white/5'
                            }`}
                            data-testid={`geo-currency-pick-${code}`}
                          >
                            <span>{cfg.flag}</span>
                            <span className="font-mono">{cfg.symbol}</span>
                            <span>{code}</span>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={handleDismiss}
                  className="text-slate-500 hover:text-slate-300 transition-colors p-1"
                  data-testid="geo-currency-dismiss"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
